/**
 * @file This file provides utility functions for working with Autodesk Construction Cloud regions.
 * It includes a mapping of region codes to their corresponding URLs, as well as functions for building URLs and validating regions.
 */

// Region configuration mapping for Autodesk ACC
export const REGIONS = {
    US: {
        code: 'US',
        name: 'United States',
        accUrl: 'https://acc.autodesk.com'
    },
    EMEA: {
        code: 'EMEA',
        name: 'Europe, Middle East & Africa',
        accUrl: 'https://acc.autodesk.eu'
    },
    APAC: {
        code: 'APAC',
        name: 'Asia Pacific',
        accUrl: 'https://acc.apac.autodesk.com'
    }
};

/**
 * Get ACC URL for a specific region
 * @param {string} regionCode - The region code (US, EMEA, APAC)
 * @returns {string} The ACC URL for the region
 */
export const getAccUrlForRegion = (regionCode) => {
    const region = REGIONS[regionCode];
    return region ? region.accUrl : REGIONS.US.accUrl; // Default to US if region not found
};

/**
 * Build complete asset URL for ACC
 * @param {string} regionCode - The region code (US, EMEA, APAC)
 * @param {string} projectId - The project ID (with or without b. prefix)
 * @param {string} assetId - The asset ID
 * @returns {string} Complete asset URL
 */
export const buildAssetUrl = (regionCode, projectId, assetId) => {
    const baseUrl = getAccUrlForRegion(regionCode);
    const cleanProjectId = projectId.replace(/^b\./, ''); // Remove b. prefix if present
    return `${baseUrl}/build/assets/projects/${cleanProjectId}/assets?assetId=${assetId}`;
};

/**
 * Build URL to access a document in ACC Build files
 * @param {string} regionCode - The region code (US, EMEA, APAC)
 * @param {string} projectId - The project ID (with or without b. prefix)
 * @param {string} folderUrn - The URN of the folder containing the file
 * @param {string} entityId - The lineage or version URN of the file
 * @returns {string} Complete file URL
 */
export const buildFileUrl = (regionCode, projectId, folderUrn, entityId) => {
    const baseUrl = getAccUrlForRegion(regionCode);
    const cleanProjectId = projectId.replace(/^b\./, '');
    const encodedFolder = encodeURIComponent(folderUrn);

    const encodedEntity = encodeURIComponent(entityId);
    return `${baseUrl}/docs/files/projects/${cleanProjectId}?folderUrn=${encodedFolder}&entityId=${encodedEntity}`;
};

/**
 * Build URL to access a folder in ACC Build files
 * @param {string} regionCode - The region code (US, EMEA, APAC)
 * @param {string} projectId - The project ID (with or without b. prefix)
 * @param {string} folderUrn - The URN of the folder to open
 * @returns {string} Complete folder URL
 */
export const buildFolderUrl = (regionCode, projectId, folderUrn) => {
    const baseUrl = getAccUrlForRegion(regionCode);
    const cleanProjectId = projectId.replace(/^b\./, '');
    const encodedFolder = encodeURIComponent(folderUrn);
    return `${baseUrl}/docs/files/projects/${cleanProjectId}?folderUrn=${encodedFolder}`;
};

/**
 * Get region object by code
 * @param {string} regionCode - The region code
 * @returns {object} Region object with code, name, and accUrl
 */
export const getRegionByCode = (regionCode) => {
    return REGIONS[regionCode] || REGIONS.US; // Default to US if not found
};

/**
 * Get all available regions
 * @returns {object} All regions configuration
 */
export const getAllRegions = () => {
    return REGIONS;
};

/**
 * Check if a region code is valid
 * @param {string} regionCode - The region code to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidRegion = (regionCode) => {
    return Object.keys(REGIONS).includes(regionCode);
};